import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, UserPlus, TrendingUp, Heart, Flame, Loader2, X, BarChart3, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const ParentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'parent') { navigate('/dashboard'); return; }
    load();
  }, [user, navigate]);

  const load = async () => {
    try { const r = await apiService.parentChildren(); setChildren(r.data.children); }
    catch (e) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const openChild = async (id) => {
    try { const r = await apiService.studentProgress(id); setSelected(r.data); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-pink-500" /></div>;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="parent-dashboard">
      <div className="bg-gradient-to-r from-pink-600 via-rose-700 to-pink-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-white">Parent Portal</h1>
              <p className="text-white/70 text-sm">Welcome, {user?.username}</p>
            </div>
            <button onClick={logout} className="px-4 py-2 bg-white/10 text-white rounded-xl font-bold text-sm hover:bg-white/20">Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black text-[#1E293B]">My Children</h2>
          <motion.button onClick={() => setShowLink(true)} whileHover={{ scale: 1.05 }} data-testid="link-child-btn"
            className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl font-bold text-sm flex items-center gap-1.5 shadow">
            <UserPlus className="w-4 h-4" />Link a Child
          </motion.button>
        </div>

        {children.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border-2 border-slate-100 shadow-md">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-3" />
            <p className="font-bold text-slate-600 mb-1">No children linked yet</p>
            <p className="text-sm text-slate-500">Link your child by their SkillRise email to see their progress</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {children.map(c => (
              <motion.button key={c.id} onClick={() => openChild(c.id)} whileHover={{ y: -3 }}
                className="bg-white rounded-2xl border-2 border-slate-100 shadow-md p-5 text-left hover:shadow-xl transition-all"
                data-testid={`child-${c.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-rose-600 flex items-center justify-center font-black text-white text-xl">
                    {c.username[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-[#1E293B] truncate">{c.username}</p>
                    <p className="text-xs text-[#64748B]">Grade {c.grade_level} · Level {c.level}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 rounded-lg py-2">
                    <p className="text-lg font-black text-green-600">{c.accuracy}%</p>
                    <p className="text-[9px] font-bold text-slate-500">ACCURACY</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg py-2">
                    <p className="text-lg font-black text-orange-600 flex items-center justify-center gap-0.5"><Flame className="w-3 h-3" />{c.current_streak}</p>
                    <p className="text-[9px] font-bold text-slate-500">STREAK</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg py-2">
                    <p className="text-lg font-black text-blue-600">{c.experience_points}</p>
                    <p className="text-[9px] font-bold text-slate-500">XP</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showLink && <LinkChildModal onClose={() => setShowLink(false)} onLinked={() => { setShowLink(false); load(); }} />}
        {selected && <ChildDetail data={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
};

const LinkChildModal = ({ onClose, onLinked }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await apiService.parentLink({ student_email: email });
      toast.success(`Linked to ${r.data.student_name}`);
      onLinked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-black text-[#1E293B]">Link Your Child</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#64748B] mb-3">Enter your child's SkillRise email to link your account.</p>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="child@email.com"
          className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl mb-3 focus:border-pink-500 outline-none"
          data-testid="parent-link-email" />
        <button onClick={submit} disabled={submitting} className="w-full py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl font-bold disabled:opacity-50"
          data-testid="parent-link-submit">
          {submitting ? 'Linking...' : 'Link Account'}
        </button>
      </motion.div>
    </motion.div>
  );
};

const ChildDetail = ({ data, onClose }) => {
  const s = data.student;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <motion.div initial={{ y: 200 }} animate={{ y: 0 }} exit={{ y: 200 }}
        className="bg-white rounded-t-3xl sm:rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-black text-[#1E293B]">{s.username}'s Progress</h2>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        
        <div className="mb-5">
          <h3 className="font-black mb-2 flex items-center gap-2 text-sm"><BarChart3 className="w-4 h-4" />Subject Performance</h3>
          {data.subject_stats.map(stat => {
            const acc = stat.questions_answered > 0 ? Math.round((stat.correct_answers / stat.questions_answered) * 100) : 0;
            return (
              <div key={stat.subject} className="bg-slate-50 rounded-xl p-3 mb-2">
                <div className="flex justify-between text-sm font-bold mb-1"><span className="capitalize">{stat.subject}</span><span className={acc >= 70 ? 'text-green-600' : acc >= 50 ? 'text-amber-600' : 'text-red-500'}>{acc}%</span></div>
                <div className="bg-slate-200 rounded-full h-2"><div className={`h-2 rounded-full ${acc >= 70 ? 'bg-green-500' : acc >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${acc}%` }}></div></div>
              </div>
            );
          })}
        </div>

        {data.emotional_history.length > 0 && (
          <div className="mb-5">
            <h3 className="font-black mb-2 flex items-center gap-2 text-sm"><Heart className="w-4 h-4" />How They've Been Feeling</h3>
            <div className="flex gap-2 flex-wrap">
              {data.emotional_history.map(e => {
                const moodEmoji = { happy: '😊', okay: '😌', stressed: '😰', confused: '🤔', sad: '😢' }[e.mood] || '😐';
                const date = new Date(e.created_at).toLocaleDateString();
                return <div key={e.id} className="px-3 py-1.5 bg-pink-50 rounded-full text-xs font-bold flex items-center gap-1">{moodEmoji} {e.mood} <span className="text-pink-400">·</span> <span className="text-slate-500">{date}</span></div>;
              })}
            </div>
          </div>
        )}

        {data.recent_tests.length > 0 && (
          <div>
            <h3 className="font-black mb-2 flex items-center gap-2 text-sm"><BookOpen className="w-4 h-4" />Recent Tests</h3>
            <div className="space-y-2">
              {data.recent_tests.slice(0, 5).map(t => (
                <div key={t.id} className={`p-2 rounded-lg text-xs flex justify-between ${t.passed ? 'bg-green-50' : 'bg-red-50'}`}>
                  <span className="font-bold capitalize">{t.subject} · {t.chapter_id}</span>
                  <span className={`font-black ${t.passed ? 'text-green-600' : 'text-red-600'}`}>{t.percentage}% {t.passed ? '✓' : '✗'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ParentDashboard;
